import { useEffect, useRef } from "react";
import { track, trackPageView } from "../lib/tracking";

/**
 * Fires ViewContent on mount, then emits ScrollDepth and TimeOnPage events as
 * the user engages. Also fires a HighIntent event after 60s on the page.
 *
 * Use on any page where you want to build a retargeting audience.
 *
 * @param {string} contentName — e.g. "Pricing", "Instagram Services"
 * @param {object} options
 *   - category: string (e.g. "pricing", "service", "platform")
 *   - value: number (product price in USD for value-based audiences)
 */
export function usePageTracking(contentName, options = {}) {
  const depthMilestones = useRef(new Set());
  const timeMilestones = useRef(new Set());
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!contentName) return;

    trackPageView(contentName, options);

    // Scroll depth: 25 / 50 / 75 / 100
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      const pct = Math.ceil((doc.scrollTop / scrollable) * 100);
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !depthMilestones.current.has(m)) {
          depthMilestones.current.add(m);
          track("ScrollDepth", {
            depth: m,
            content_name: contentName,
          });
        }
      });
    };

    // Time on page: 30s / 60s / 120s
    const timers = [30, 60, 120].map((s) =>
      setTimeout(() => {
        if (timeMilestones.current.has(s)) return;
        timeMilestones.current.add(s);
        track("TimeOnPage", { seconds: s, content_name: contentName });

        // At 60s on a high-intent page, fire HighIntent (seed retargeting audience)
        if (s === 60 && (options.category === "pricing" || options.category === "service")) {
          track("HighIntent", {
            content_name: contentName,
            content_category: options.category,
          });
        }
      }, s * 1000)
    );

    // Exit intent — cursor moves to top edge of viewport
    let exitFired = false;
    const onMouseLeave = (e) => {
      if (exitFired) return;
      if (e.clientY <= 0) {
        exitFired = true;
        track("ExitIntent", {
          content_name: contentName,
          seconds_on_page: Math.round((Date.now() - startedAt.current) / 1000),
        });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onMouseLeave);
      timers.forEach((t) => clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentName]);
}
